import { useState } from 'react';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { Button } from '../../shared/ui/Button';
import { Modal } from '../../shared/ui/Modal';
import { ConfirmModal } from '../../shared/ui/ConfirmModal';
import { Skeleton } from '../../shared/ui/Skeleton';
import { EmptyState } from '../../shared/ui/EmptyState';
import { ErrorState } from '../../shared/ui/ErrorState';
import { useToast } from '../../shared/ui/Toast';
import { AddressField } from '../../shared/ui/AddressField';
import {
  useSavedLocations,
  useAddSavedLocation,
  useDeleteSavedLocation,
} from '../../features/saved-locations/hooks';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

type GeoResult = { address: string; lat: number; lng: number };

const labelClass = 'block text-[11px] font-bold text-muted uppercase tracking-widest mb-2';

export function SavedLocationsPage() {
  usePageMeta('Saved Locations');
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [locLabel, setLocLabel] = useState('');
  const [selected, setSelected] = useState<GeoResult | null>(null);
  const [saveAttempted, setSaveAttempted] = useState(false);

  const { data: locations = [], isLoading, isError, refetch } = useSavedLocations();
  const addLocation = useAddSavedLocation();
  const deleteLocation = useDeleteSavedLocation();
  const { toast } = useToast();

  const closeModal = () => { setAddOpen(false); setLocLabel(''); setSelected(null); setSaveAttempted(false); };

  const handleAddressChange = (address: string, coords: [number, number] | null) => {
    if (coords) {
      setSelected({ address, lat: coords[0], lng: coords[1] });
      if (!locLabel) setLocLabel(address.split(',')[0].trim());
    } else {
      setSelected(null);
    }
  };

  const handleAdd = async () => {
    if (!locLabel.trim() || !selected) {
      setSaveAttempted(true);
      return;
    }
    try {
      await addLocation.mutateAsync({ label: locLabel.trim(), address: selected.address, lat: selected.lat, lng: selected.lng });
      toast('Location saved!', 'success');
      closeModal();
    } catch (err) {
      toast((err as Error).message || 'Failed to save location', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLocation.mutateAsync(deleteTarget);
      toast('Location removed.', 'info');
      setDeleteTarget(null);
    } catch (err) {
      toast((err as Error).message || 'Failed to remove location', 'error');
    }
  };

  return (
    <div className="flex flex-col max-w-2xl mx-auto w-full">

      {/* Header */}
      <div className="pb-5 flex items-end justify-between">
        <div>
          <p className="text-xs font-bold text-muted uppercase tracking-widest mb-1">Customer</p>
          <h1 className="text-2xl font-black text-text">Saved Locations</h1>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setAddOpen(true)}
          className="!text-accent normal-case !tracking-normal !border-2 !border-accent/30 rounded-xl hover:!bg-accent hover:!text-black"
        >
          <Plus size={13} />
          Add Location
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="rounded-xl overflow-hidden bg-surface-raised/40">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-start gap-4 px-4 py-4">
              <Skeleton className="h-8 w-8 rounded-xl flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="py-12">
          <ErrorState onRetry={() => refetch()} />
        </div>
      ) : locations.length === 0 ? (
        <div className="py-20">
          <EmptyState
            title="No saved locations"
            description="Save your frequently used addresses to speed up booking."
            icon={<MapPin size={24} />}
          />
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden bg-surface-raised/40">
          {locations.map((loc, i) => (
            <AnimateIn key={loc.id} animation="fade-up" delay={i * 60} className="w-full">
            <div className="flex items-start gap-4 px-4 py-4 hover:bg-surface-raised transition-colors">
              <div className="h-8 w-8 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin size={15} className="text-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-text">{loc.label}</p>
                <p className="text-xs text-muted mt-0.5 truncate">{loc.address}</p>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDeleteTarget(loc.id)}
                className="!text-muted hover:!text-danger hover:!bg-transparent flex-shrink-0 mt-0.5"
                aria-label="Delete location"
              >
                <Trash2 size={15} />
              </Button>
            </div>
            </AnimateIn>
          ))}
        </div>
      )}

      {/* Delete location confirmation */}
      <ConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Remove this location?"
        message="This saved location will be permanently removed."
        confirmLabel="Remove"
        danger
        loading={deleteLocation.isPending}
      />

      {/* Add location modal */}
      <Modal open={addOpen} onClose={closeModal} title="Add saved location" hideCloseButton>
        <div className="flex flex-col gap-4">
          <div>
            <label className={labelClass}>Label (e.g. "Work", "Home")</label>
            <input
              type="text"
              value={locLabel}
              onChange={(e) => setLocLabel(e.target.value)}
              placeholder="Give this place a name"
              className="w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors"
            />
            {saveAttempted && !locLabel.trim() && (
              <p className="text-xs text-danger mt-1.5">Required</p>
            )}
          </div>

          <div>
            <AddressField
              label="Address"
              value={selected?.address ?? ''}
              placeholder="Search for an address…"
              onChange={handleAddressChange}
            />
            {saveAttempted && !selected && (
              <p className="text-xs text-danger mt-1.5">Select an address from the suggestions.</p>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button
              onClick={handleAdd}
              loading={addLocation.isPending}
              loadingText="Saving…"
              className={!locLabel.trim() || !selected ? '!opacity-30' : ''}
            >
              Save Location
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
